import React, { useState } from "react";
import { Button, Input, Modal, Table, Tabs, InputNumber } from "antd";
import { Search, Plus, Minus } from "lucide-react";
import { SuppliesType } from "~/utils/constants";
import { useGetSupplies } from "~/services/supply";
import { TSupply, TServiceTicketSupplyItem } from "~/types";
import { useDebounceValue } from "~/hooks/use-debounce";

interface AddSuppliesModalProps {
  open: boolean;
  onCancel: () => void;
  onSave: (
    selectedSupplies: Record<string, { supply: TSupply; quantity: number }>
  ) => void;
  existingSupplies?: TServiceTicketSupplyItem[];
  loading?: boolean;
}

export const AddSuppliesModal: React.FC<AddSuppliesModalProps> = ({
  open,
  onCancel,
  onSave,
  existingSupplies = [],
  loading = false,
}) => {
  const [addSuppliesSearch, setAddSuppliesSearch] = useState("");
  const [search] = useDebounceValue(addSuppliesSearch, 500);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [selectedSupplies, setSelectedSupplies] = useState<
    Record<string, { supply: TSupply; quantity: number }>
  >({});
  const [inputQuantities, setInputQuantities] = useState<
    Record<string, number>
  >({});

  // Fetch available supplies for adding
  const { data: availableSupplies, isLoading: loadingAvailableSupplies } =
    useGetSupplies(
      {
        search,
        suppliesType:
          activeTab !== "all" ? (activeTab as SuppliesType) : undefined,
        page,
        pageSize: 10,
      },
      open
    );

  const handleAddNewSupply = (supply: TSupply, quantity: number = 1) => {
    const existingIndex = Object.keys(selectedSupplies).findIndex(
      (key) => key === supply.id
    );

    if (existingIndex >= 0) {
      // Update existing quantity
      setSelectedSupplies((prev) => ({
        ...prev,
        [supply.id]: {
          supply,
          quantity: prev[supply.id].quantity + quantity,
        },
      }));
    } else {
      // Add new supply item
      setSelectedSupplies((prev) => ({
        ...prev,
        [supply.id]: {
          supply,
          quantity,
        },
      }));
    }
  };

  const handleRemoveSelectedSupply = (supplyId: string) => {
    setSelectedSupplies((prev) => {
      const updated = { ...prev };
      delete updated[supplyId];
      return updated;
    });

    // Reset the input quantity back to 1 when removing a supply
    setInputQuantities((prev) => ({
      ...prev,
      [supplyId]: 1,
    }));
  };

  const handleQuantityChangeInModal = (supplyId: string, quantity: number) => {
    if (quantity <= 0) return;
    setSelectedSupplies((prev) => ({
      ...prev,
      [supplyId]: {
        ...prev[supplyId],
        quantity,
      },
    }));
  };

  const handleInputQuantityChange = (supplyId: string, quantity: number) => {
    setInputQuantities((prev) => ({
      ...prev,
      [supplyId]: quantity,
    }));
  };

  const handleSave = () => {
    onSave(selectedSupplies);
    setSelectedSupplies({});
    setInputQuantities({});
    setAddSuppliesSearch("");
    setActiveTab("all");
  };

  const handleCancel = () => {
    setSelectedSupplies({});
    setInputQuantities({});
    setAddSuppliesSearch("");
    setActiveTab("all");
    onCancel();
  };

  const columns = [
    {
      title: "Supply",
      dataIndex: "name",
      key: "name",
      render: (_: any, record: TSupply) => (
        <div>
          <div className="font-medium">{record.name}</div>
          <div className="text-xs text-gray-500">
            {record.brand && `${record.brand}`}
            {record.brand && record.category && ` · `}
            {record.category && `${record.category}`}
          </div>
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "",
      key: "quantity",
      align: "center" as const,
      width: 180,
      render: (_: any, supply: TSupply) => {
        const supplyItem = existingSupplies?.find(
          (item) => item.supplyId === supply.id
        );

        const selectedItem = selectedSupplies[supply.id];
        const currentQuantity =
          selectedItem?.quantity || supplyItem?.quantity || 1;
        const maxStock = supply.currentStock || 0;

        if (selectedItem || supplyItem) {
          return (
            <div className="flex items-center justify-center gap-1">
              <Button
                size="small"
                onClick={() =>
                  handleQuantityChangeInModal(
                    supply.id,
                    Math.max(1, currentQuantity - 1)
                  )
                }
                disabled={currentQuantity <= 1}
                icon={<Minus size={12} />}
              />
              <InputNumber
                size="small"
                min={1}
                max={maxStock}
                value={currentQuantity}
                onChange={(value) =>
                  handleQuantityChangeInModal(supply.id, value || 1)
                }
                className="w-14"
                controls={false}
              />
              <Button
                size="small"
                onClick={() =>
                  handleQuantityChangeInModal(
                    supply.id,
                    Math.min(maxStock, currentQuantity + 1)
                  )
                }
                disabled={currentQuantity >= maxStock}
                icon={<Plus size={12} />}
              />
            </div>
          );
        }

        return (
          <InputNumber
            size="small"
            min={1}
            max={maxStock}
            defaultValue={1}
            value={inputQuantities[supply.id] || 1}
            onChange={(value) =>
              handleInputQuantityChange(supply.id, value || 1)
            }
            placeholder="Qty"
            disabled={maxStock <= 0}
            className="w-14"
            controls={false}
          />
        );
      },
    },
    {
      title: "",
      dataIndex: "",
      key: "action",
      align: "center" as const,
      width: 100,
      render: (_: any, supply: TSupply) => {
        const selectedItem = selectedSupplies[supply.id];
        const quantity = inputQuantities[supply.id] || 1;

        // Check if this supply is already in the existing supplies list
        const alreadyExists = existingSupplies?.some(
          (item) => item.supplyId === supply.id
        );

        return (
          <Button
            disabled={(supply.currentStock ?? 0) <= 0 || alreadyExists}
            size="small"
            htmlType="button"
            onClick={() => {
              if (selectedItem) {
                handleRemoveSelectedSupply(supply.id);
              } else {
                handleAddNewSupply(supply, quantity);
              }
            }}
            type={selectedItem ? "default" : "primary"}
          >
            {alreadyExists ? "Added" : selectedItem ? "Remove" : "Add"}
          </Button>
        );
      },
    },
  ];

  return (
    <Modal
      title="Add Supplies to Service Ticket"
      open={open}
      onCancel={handleCancel}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleSave}
            disabled={Object.keys(selectedSupplies).length === 0}
            loading={loading}
          >
            Save ({Object.keys(selectedSupplies).length})
          </Button>
        </div>
      }
      width="95%"
      style={{ maxWidth: 800 }}
    >
      <div className="mb-4">
        <Input
          prefix={<Search className="mr-1 text-gray-400" />}
          placeholder="Search supplies..."
          value={addSuppliesSearch}
          onChange={(e) => setAddSuppliesSearch(e.target.value)}
          className="w-full"
        />
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: "all", label: "All" },
          {
            key: SuppliesType.TOOLS_KITS_AND_HARDWARE,
            label: "Tools, Kits & Hardware",
          },
          { key: SuppliesType.QUICK_PACK, label: "Quick Packs" },
          {
            key: SuppliesType.INTERNAL_SUPPLIES_AND_FACILITIES,
            label: "Internal Supplies & Facilities",
          },
          {
            key: SuppliesType.PACKAGING_MATERIALS,
            label: "Packaging Materials",
          },
          { key: SuppliesType.PPE, label: "PPE" },
          { key: SuppliesType.EQUIPMENT, label: "Equipment" },
        ]}
      />

      <Table
        columns={columns}
        dataSource={availableSupplies?.items ?? []}
        rowKey="id"
        loading={loadingAvailableSupplies}
        pagination={{
          current: page,
          pageSize: 10,
          total: availableSupplies?.meta?.totalItems ?? 0,
          onChange: setPage,
          showSizeChanger: false,
        }}
        size="small"
      />
    </Modal>
  );
};
